import { useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { CountriesRepository } from '@packlink/packlink-sdk';
import { FieldMessageType, FormField, SelectAutocomplete } from '@shipengine/giger';
import { jsx } from '@emotion/react/jsx-runtime';
import { useSelectAutocompleteMessages } from '../hooks/useSelectAutocompleteMessages.js';

const QUERY_KEY = 'countrySelector';
const CountrySelector = _ref => {
  let {
    isInvalid,
    errorMessage,
    hintMessage,
    value,
    labelTranslationKey,
    onChange: onChangeProp,
    onLoad,
    ...rest
  } = _ref;
  const sdk = useSdk();
  const {
    t,
    i18n
  } = useTranslation();
  const {
    noResults: noResultsLabel
  } = useSelectAutocompleteMessages();
  const CountriesRepository$1 = useMemo(() => new CountriesRepository(sdk.apiClient), [sdk.apiClient]);
  const getCountryByAlpha2Code = useCallback(countries => alpha2Code => countries === null || countries === void 0 ? void 0 : countries.find(countryOption => countryOption.alpha2Code === alpha2Code), []);
  const loadValue = useCallback((countries, country) => {
    if (country) {
      const selectedCountry = getCountryByAlpha2Code(countries)(country);
      onLoad === null || onLoad === void 0 ? void 0 : onLoad(selectedCountry);
    }
  }, [getCountryByAlpha2Code, onLoad]);
  const {
    data: countries
  } = useQuery({
    queryKey: [QUERY_KEY, i18n.language],
    queryFn: () => CountriesRepository$1.all({
      language: i18n.language
    }).then(countries => {
      loadValue(countries, value === null || value === void 0 ? void 0 : value.value);
      return countries;
    }),
    enabled: !!i18n.language,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  // Set label of initial value if SDK call is cached by ReactQuery
  useEffectOnce(() => {
    if (countries) {
      loadValue(countries, value === null || value === void 0 ? void 0 : value.value);
    }
  });
  useEffect(() => {
    // Set label for unlabeled values (this usually happens when initialValues of hosting form are reinitialized)
    const isIncompleteCountry = (value === null || value === void 0 ? void 0 : value.value) && !(value !== null && value !== void 0 && value.label);
    if (isIncompleteCountry && countries) {
      loadValue(countries, value === null || value === void 0 ? void 0 : value.value);
    }
  }, [countries, value === null || value === void 0 ? void 0 : value.value, value === null || value === void 0 ? void 0 : value.label, loadValue]);
  const countryOptions = useMemo(() => (countries === null || countries === void 0 ? void 0 : countries.map(transformCountryToCountryOption)) || [], [countries]);
  const handleOnChange = useCallback((name, value) => {
    const selectedCountry = value && countries ? getCountryByAlpha2Code(countries)(value.value) : null;
    onChangeProp(name, value, selectedCountry || null);
  }, [countries, getCountryByAlpha2Code, onChangeProp]);
  const formFieldMessage = useMemo(() => {
    if (hintMessage && !isInvalid) {
      return {
        type: FieldMessageType.HINT,
        content: hintMessage
      };
    } else if (isInvalid && errorMessage) {
      return {
        type: FieldMessageType.ERROR,
        content: errorMessage
      };
    }
    return undefined;
  }, [errorMessage, hintMessage, isInvalid]);
  return jsx(FormField, {
    message: formFieldMessage,
    children: jsx(SelectAutocomplete, {
      value: value,
      isInvalid: isInvalid,
      options: countryOptions,
      noResultsLabel: noResultsLabel,
      label: labelTranslationKey ? t(labelTranslationKey) : t('address-selectors.country.label'),
      onChange: handleOnChange,
      ...rest
    })
  });
};
function transformCountryToCountryOption(country) {
  return {
    label: country.name,
    value: country.alpha2Code
  };
}

export { CountrySelector, transformCountryToCountryOption };
//# sourceMappingURL=CountrySelector.js.map
